import React, { FC } from 'react';
import styled from 'styled-components';
import signature from '../../../assets/logos/nasos-logo-svg.svg';
import { HandCardDef } from './handModel';
import { BRICK, DECK_INK, DECK_PAPER, MINT, paper, RARITY_COLOR, RARITY_PIPS, SERIF } from './deck';

const Body = styled.div<{ $w: number; $h: number }>`
  position: relative;
  width: ${(p) => p.$w}px;
  height: ${(p) => p.$h}px;
  flex-shrink: 0;
`;

const Column = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 14px 10px 16px;
  text-align: center;
`;

const Index = styled.span<{ $size: number }>`
  position: absolute;
  font-size: ${(p) => p.$size}px;
  line-height: 1;
`;

const When = styled.div<{ $size: number }>`
  font-family: ${SERIF};
  font-style: italic;
  font-weight: 600;
  font-size: ${(p) => p.$size}px;
  line-height: 1.1;
  color: #7a7264;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

interface HandCardProps {
  card: HandCardDef | null;
  w: number;
  h: number;
  /** a line in place of "when it acts" (shown on larger cards) */
  note?: string;
  /** a mint tab on the bottom edge, e.g. "play now"; shown at every size */
  mark?: string;
}

/**
 * The tier as a column of diamond pips: hollow ink for a common, filled in the
 * tier colour above that.
 */
const Pips: FC<{ rarity: string; size: number; style: React.CSSProperties }> = ({ rarity, size, style }) => {
  const filled = rarity !== 'common';
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: Math.round(size * 0.55),
        ...style,
      }}
    >
      {Array.from({ length: RARITY_PIPS[rarity] ?? 1 }, (_, i) => (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            boxSizing: 'border-box',
            transform: 'rotate(45deg)',
            background: filled ? RARITY_COLOR[rarity] : 'transparent',
            border: `1px solid ${filled ? RARITY_COLOR[rarity] : DECK_INK}`,
          }}
        />
      ))}
    </span>
  );
};

/** Fit the longest word of a name inside the card. */
function nameSize(name: string, w: number, max: number): number {
  const longest = Math.max(...name.split(' ').map((p) => p.length));
  return Math.min(max, (w - 14) / (longest * 0.5));
}

/**
 * One card: emoji in two opposite corners and in the middle, the tier pips in
 * the top-right corner, the name in serif with a thin rarity rule under it,
 * the inner frame in the tier colour, the signature mark, and (on larger
 * cards) when it acts.
 */
const HandCard: FC<HandCardProps> = ({ card, w, h, note, mark }) => {
  const big = w >= 110;
  if (!card) {
    return (
      <Body $w={w} $h={h}>
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: 7, border: '1px dashed #cfc8b8' }} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 15,
            color: '#b9b1a1',
          }}
        >
          {note ?? 'empty'}
        </div>
      </Body>
    );
  }
  const idx = big ? 15 : 11;
  const pip = big ? 5 : w >= 80 ? 4.5 : 3.5;
  return (
    <Body $w={w} $h={h}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, ...paper(true, card.rarity) }} />
      <Index $size={idx} style={{ top: 6, left: 7 }} aria-hidden="true">
        {card.emoji}
      </Index>
      <Index $size={idx} style={{ bottom: 6, right: 7, transform: 'rotate(180deg)' }} aria-hidden="true">
        {card.emoji}
      </Index>
      {/* the free corner: under an index the pips run into long names */}
      <Pips rarity={card.rarity} size={pip} style={{ top: 7, right: 7, width: idx }} />
      <Column>
        <div style={{ fontSize: Math.round(w * 0.32), lineHeight: 1 }}>{card.emoji}</div>
        <span
          style={{
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: nameSize(card.name, w, big ? 19 : 13.5),
            lineHeight: 1.05,
            color: DECK_INK,
          }}
        >
          {card.name}
        </span>
        <span aria-hidden="true" style={{ width: '46%', height: 2, background: RARITY_COLOR[card.rarity], borderRadius: 1 }} />
        {/* the corners are busy on small cards, so only larger ones say when it acts */}
        {w >= 100 && <When $size={big ? 14 : 11}>{note ?? card.when}</When>}
      </Column>
      <img
        src={signature}
        alt=""
        aria-hidden="true"
        style={{ position: 'absolute', left: 7, bottom: 6, width: big ? 30 : 20, opacity: 0.55 }}
      />
      {card.isNew && (
        <span
          style={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 11,
            background: BRICK,
            color: DECK_PAPER,
            padding: '0 7px',
            borderRadius: 8,
          }}
        >
          new
        </span>
      )}
      {mark && (
        <span
          style={{
            position: 'absolute',
            bottom: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            fontFamily: SERIF,
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 11,
            lineHeight: '14px',
            whiteSpace: 'nowrap',
            background: MINT,
            color: DECK_INK,
            border: `1px solid ${DECK_INK}`,
            padding: '0 6px',
            borderRadius: 8,
          }}
        >
          {mark}
        </span>
      )}
    </Body>
  );
};

export default HandCard;
